import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Polyline, CircleMarker, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import styled from 'styled-components';
import useGetData from '../Hooks/useGetData';
import { beWhereApi } from '../Services/BeWhere';
import { decode } from '../Services/DecodeToken';

export interface BeWhereSnapshot {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  speed: number;
  direction: number;
  batteryLevel: number;
  rssi: number;
  temperature: number;
  humidity: number;
  timestamp: number;
  gpsTimestamp: number;
  received: number;
  address: string;
  status: string;
  eventType: string;
  fix: number;
  numSvc: number;
  hdop: number;
  altitude: number;
  distance: number;
  networkLocation?: {
    latitude: number;
    longitude: number;
    accuracy: number;
  };
  serialNumber: string;
}



export interface GpsDevicePoint {
  id: number;
  device_id: string;
  name: string;
  voltage: string | number;
  gnss_run_status: number;
  fix_status: number;
  utc_datetime: string; 
  latitude: string;
  longitude: string;
  altitude: number | null;
  speed_over_ground: number | null;
  course_over_ground: number | null;
  fix_mode: number;
  hdop: number | null;
  pdop: number | null;
  vdop: number | null;
  satellites_in_view: number;
  cn0_max: number | null;
  hpa: string;
  recorded_at: string;
}

export interface GpsDeviceCompany {
  device_id: string;
  asset_id: string;
  name?: string;
  asset_name?: string;
  device_name?: string;
  fleet_name?: string;
}

export interface Asset {
  id: string;
  name: string;
}


const UpdateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #0098aa;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background: #2563EB;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    flex-shrink: 0;
  }
  
  .animate-spin {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const UpdateTime = styled.div`
  font-size: 0.75rem;
  color: #6B7280;
  font-style: italic;
`;

const PanelToggleContainer = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  z-index: 1001;
`;

const ToggleButton = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: ${props => props.active ? '#0098aa' : 'white'};
  color: ${props => props.active ? 'white' : '#6B7280'};
  border: 2px solid ${props => props.active ? '#0098aa' : '#E5E7EB'};
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: ${props => props.active ? '#007d8a' : '#F9FAFB'};
    border-color: ${props => props.active ? '#007d8a' : '#D1D5DB'};
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  
  h3 {
    margin: 0;
    fontSize: 1.125rem;
    fontWeight: 600;
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: transparent;
  color: #6B7280;
  border: none;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #F3F4F6;
    color: #374151;
  }
`;

const LatestDataPanel = styled.div`
  position: absolute;
  top: 20px;
  right: 70px;
  background: white;
  border-radius: 0.5rem;
  padding: 1rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  width: 300px;
  max-height: 400px;
  overflow-y: auto;
  z-index: 1000;
  animation: slideIn 0.3s ease-out;
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @media (max-width: 768px) {
    position: relative;
    top: 0;
    right: 0;
    margin: 1rem;
    width: auto;
    max-height: none;
  }
`;

const StatusBadge = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.75rem;
  background: ${props => props.color}20;
  color: ${props => props.color};
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 1rem;
  width: fit-content;
`;

const StatusDot = styled.div<{ color: string }>`
  width: 0.5rem;
  height: 0.5rem;
  background: ${props => props.color};
  border-radius: 50%;
`;

const LatestDataGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const DataItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const DataLabel = styled.span`
  font-size: 0.75rem;
  color: #6B7280;
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

const DataValue = styled.span`
  font-size: 0.875rem;
  color: #111827;
  font-weight: 600;
`;

const StatsPanel = styled.div`
  position: absolute;
  bottom: 80px;
  right: 70px;
  background: white;
  border-radius: 0.5rem;
  padding: 1rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  width: 280px;
  z-index: 1000;
  animation: slideIn 0.3s ease-out;
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @media (max-width: 768px) {
    position: relative;
    bottom: 0;
    right: 0;
    margin: 1rem;
    width: auto;
  }
`;

const StatItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #F3F4F6;
  
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  
  &:first-child {
    padding-top: 0;
  }
  
  .label {
    font-size: 0.875rem;
    color: #6B7280;
    font-weight: 500;
  }
  
  .value {
    font-size: 0.875rem;
    color: #111827;
    font-weight: 600;
  }
`;

const EmptyMessage = styled.div`
  color: #6B7280;
  font-size: 0.875rem;
  font-style: italic;
  text-align: center;
  padding: 1rem 0;
`;

const PageContainer = styled.div`
  flex: 1;
  /* Viewport-based floor so the map fills the page even when the app shell
     doesn't give this page a definite height (prod/main layout). */
  min-height: calc(100vh - 60px);
  display: flex;
  flex-direction: column;
  background: #f8fafc;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const Header = styled.header`
  background: linear-gradient(135deg, #0098aa 0%, #007b8a 100%);
  color: white;
  padding: 1.5rem 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  position: relative;

  h1 {
    margin: 0;
    font-size: 1.75rem;
    font-weight: 600;
  }

  .subtitle {
    margin-top: 0.5rem;
    font-size: 0.9rem;
    color: white;
  }

  @media (max-width: 899px) {
    padding: 1rem;

    h1 {
      font-size: 1.25rem;
    }
  }
`;


const ControlsContainer = styled.div`
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 1rem 2rem;
  display: flex;
  gap: 2rem;
  align-items: center;
  flex-wrap: wrap;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  @media (max-width: 899px) {
    padding: 0.75rem 1rem;
    gap: 0.75rem;
  }
`;

const DateRangeSelector = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const DateInput = styled.input`
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;
  background: white;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &:hover {
    border-color: #cbd5e0;
  }
`;

const QuickDateButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const QuickDateButton = styled.button<{ active?: boolean }>`
  padding: 0.5rem 1rem;
  border: 2px solid ${props => props.active ? '#667eea' : '#e2e8f0'};
  background: ${props => props.active ? '#667eea' : 'white'};
  color: ${props => props.active ? 'white' : '#4a5568'};
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #667eea;
    background: ${props => props.active ? '#5a67d8' : '#f7fafc'};
  }

  &:active {
    transform: translateY(1px);
  }
`;

const MapWrapper = styled.div`
  flex: 1;
  min-height: 400px;
  position: relative;
  overflow: hidden;
  z-index: 1;
`;

/** Leaflet needs invalidateSize when the map mounts inside a flex layout (common prod-only blank map). */
const MapResizer: React.FC = () => {
  const map = useMap();

  useEffect(() => {
    const resize = () => map.invalidateSize();
    const timer = window.setTimeout(resize, 0);
    window.addEventListener('resize', resize);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', resize);
    };
  }, [map]);

  return null;
};

const MapBoundsUpdater: React.FC<{ positions: [number, number][] }> = ({ positions }) => {
  const map = useMap();

  useEffect(() => {
    if (positions.length === 0) return;
    map.fitBounds(L.latLngBounds(positions), { padding: [50, 50] });
  }, [map, positions]);

  return null;
};

const MapRefBridge: React.FC<{ mapRef: React.MutableRefObject<L.Map | null> }> = ({ mapRef }) => {
  const map = useMap();

  useEffect(() => {
    mapRef.current = map;
  }, [map, mapRef]);

  return null;
};



const StatusIndicator = styled.div<{ status: 'online' | 'offline' | 'warning' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
  background: ${props => {
    switch (props.status) {
      case 'online': return '#dcfce7';
      case 'offline': return '#fef2f2';
      case 'warning': return '#fef3c7';
      default: return '#f1f5f9';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'online': return '#166534';
      case 'offline': return '#dc2626';
      case 'warning': return '#d97706';
      default: return '#64748b';
    }
  }};

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
    animation: ${props => props.status === 'online' ? 'pulse 2s infinite' : 'none'};
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const Legend = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  background: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  z-index: 1000;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const LegendDot = styled.div<{ color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.color};
  border: 2px solid white;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
`;

const LegendLine = styled.div<{ color: string }>`
  width: 20px;
  height: 3px;
  background: ${props => props.color};
  border-radius: 2px;
`;

const SummaryPanel = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  background: white;
  border-radius: 0.5rem;
  padding: 1rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  width: 280px;
  max-height: 500px;
  display: flex;
  flex-direction: column;
  z-index: 1000;
  animation: slideIn 0.3s ease-out;
  @keyframes slideIn {
    from { opacity: 0; transform: translateX(-20px); }
    to { opacity: 1; transform: translateX(0); }
  }
`;

const SummaryList = styled.div`
  overflow-y: auto;
  flex: 1;
  margin-top: 0.75rem;
`;

const SummaryItem = styled.div<{ active?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 0.5rem 0.4rem;
  border-bottom: 1px solid #F3F4F6;
  border-radius: 0.25rem;
  font-size: 0.8rem;
  cursor: pointer;
  background: ${props => props.active ? '#FFF3E0' : 'transparent'};
  border-left: ${props => props.active ? '3px solid #FF6B00' : '3px solid transparent'};
  transition: background 0.15s;
  &:last-child { border-bottom: none; }
  &:hover { background: ${props => props.active ? '#FFF3E0' : '#F9FAFB'}; }
`;

const SummaryTime = styled.span`
  font-weight: 600;
  color: #111827;
`;

const SummaryCoords = styled.span`
  color: #6B7280;
  margin-top: 0.1rem;
`;

const IntervalSelector = styled.div`
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;
`;

const IntervalBtn = styled.button<{ active: boolean }>`
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  border-radius: 0.25rem;
  border: 1px solid ${props => props.active ? '#0098aa' : '#E5E7EB'};
  background: ${props => props.active ? '#0098aa' : 'white'};
  color: ${props => props.active ? 'white' : '#4B5563'};
  cursor: pointer;
  font-weight: 500;
`;

const LegendText = styled.span`
  font-size: 0.875rem;
  color: #4a5568;
  font-weight: 500;
`;

const EmptyState = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #64748b;
  padding: 2rem;
  background: rgba(248, 250, 252, 0.85);
  z-index: 500;
  pointer-events: none;

  svg {
    width: 64px;
    height: 64px;
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  h3 {
    margin: 0 0 0.5rem 0;
    font-size: 1.25rem;
    font-weight: 600;
  }

  p {
    margin: 0;
    font-size: 0.875rem;
    opacity: 0.8;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid #f3f4f6;
  border-radius: 50%;
  border-top-color: #667eea;
  animation: spin 1s ease-in-out infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// Constants
const COLORS = {
  track: '#667eea',
  success: '#10b981',
  danger: '#ef4444',
  warning: '#f59e0b',
  white: '#ffffff',
  gray: '#64748b'
};

// Helper functions
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

const formatTimestamp = (timestamp: string): string => {
  return new Date(timestamp).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
};

const formatDateForButton = (date: Date): string => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric' 
  });
};

const toLocalDateString = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const parseDateInput = (value: string, endOfDay = false) => {
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  if (endOfDay) {
    date.setHours(23, 59, 59, 999);
  }
  return date;
};



const MapComponent: React.FC = () => {
  const [searchParams] = useSearchParams();
  const deviceId = searchParams.get('deviceId');

  const [dateRange, setDateRange] = useState({
    start: toLocalDateString(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)),
    end: toLocalDateString(new Date())
  });
  const [selectedQuickRange, setSelectedQuickRange] = useState('7d');
  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<Date | null>(null);
  const [showLatestData, setShowLatestData] = useState(true);
  const [showTripStats, setShowTripStats] = useState(true);
  const [beWhereLatestData, setBeWhereLatestData] = useState<any>(null);
  const [showSummary, setShowSummary] = useState(false);
  const [summaryInterval, setSummaryInterval] = useState<number | null>(60);
  const [selectedPointId, setSelectedPointId] = useState<number | null>(null);

  const mapRef = useRef<L.Map | null>(null);

  const [devices] = useGetData<GpsDeviceCompany>('/api/gps/devices');

  const token: any = decode();
  const OVID = token.OVID;

  const [deviceData, loadingDeviceData, errorDeviceData, refreshDeviceData] = useGetData<GpsDevicePoint>(
    `/api/gps/devices/${deviceId}?start=${dateRange.start}&end=${dateRange.end}&interval=${summaryInterval ?? 60}`
  );


  const quickRanges = [
    { key: '1d', label: 'Today', days: 0 },
    { key: '7d', label: '7 Days', days: 7 },
    { key: '30d', label: '30 Days', days: 30 },
    { key: '90d', label: '90 Days', days: 90 },
  ];

  const filteredData = useMemo(() => {
    if (!Array.isArray(deviceData)) return [];

    const startDate = parseDateInput(dateRange.start);
    const endDate = parseDateInput(dateRange.end, true);

    const sorted = deviceData.filter(point => {
      const pointDate = new Date(point.recorded_at);
      return pointDate >= startDate && pointDate <= endDate;
    }).sort((a, b) => new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime());

    const valid = sorted.filter(point => {
      const lat = Number(point.latitude);
      const lon = Number(point.longitude);
      return Number.isFinite(lat) && Number.isFinite(lon) && !(lat === 0 && lon === 0);
    });

    // Cell-tower fallback can report one bad beacon hundreds of km away between
    // nearby samples. Only suppress it when the surrounding samples are close in
    // time, so legitimate day-separated travel pings still appear in broad ranges.
    const SPIKE_DISTANCE_KM = 300;
    const NEIGHBOR_AGREEMENT_KM = 50;
    const SPIKE_NEIGHBOR_WINDOW_MINUTES = 6 * 60;
    const dist = (a: GpsDevicePoint, b: GpsDevicePoint) =>
      calculateDistance(Number(a.latitude), Number(a.longitude), Number(b.latitude), Number(b.longitude));
    const minutesBetween = (a: GpsDevicePoint, b: GpsDevicePoint) =>
      Math.abs(new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime()) / (1000 * 60);
    const pointsAreNearInTime = (a: GpsDevicePoint, b: GpsDevicePoint) =>
      minutesBetween(a, b) <= SPIKE_NEIGHBOR_WINDOW_MINUTES;

    return valid.filter((point, i) => {
      const prev = valid[i - 1];
      const next = valid[i + 1];
      if (prev && next) {
        return !(
          pointsAreNearInTime(prev, point) &&
          pointsAreNearInTime(point, next) &&
          dist(prev, point) > SPIKE_DISTANCE_KM &&
          dist(point, next) > SPIKE_DISTANCE_KM &&
          dist(prev, next) < NEIGHBOR_AGREEMENT_KM
        );
      }
      if (!prev && next) {
        const next2 = valid[i + 2];
        return !(next2 &&
          pointsAreNearInTime(point, next) &&
          pointsAreNearInTime(next, next2) &&
          dist(point, next) > SPIKE_DISTANCE_KM &&
          dist(next, next2) < NEIGHBOR_AGREEMENT_KM
        );
      }
      if (prev && !next) {
        const prev2 = valid[i - 2];
        return !(prev2 &&
          pointsAreNearInTime(prev, point) &&
          pointsAreNearInTime(prev2, prev) &&
          dist(prev, point) > SPIKE_DISTANCE_KM &&
          dist(prev2, prev) < NEIGHBOR_AGREEMENT_KM
        );
      }
      return true;
    });
  }, [deviceData, dateRange]);

  const latestData = useMemo(() => {
    if (beWhereLatestData) return beWhereLatestData;
    
    if (!deviceData || !Array.isArray(deviceData) || deviceData.length === 0) return null;
    
    const sortedData = [...deviceData].sort((a, b) => 
      new Date(b.recorded_at).getTime() - new Date(a.recorded_at).getTime()
    );
    
    return sortedData[0];
  }, [deviceData, beWhereLatestData]);

  const stats = useMemo(() => {
    if (filteredData.length === 0) return { 
      totalDistance: 0, 
      avgSpeed: 0, 
      maxSpeed: 0, 
      totalPoints: 0,
      duration: 0
    };

    let totalDistance = 0;
    let totalSpeed = 0;
    let maxSpeed = 0;
    let validSpeedCount = 0;

    for (let i = 1; i < filteredData.length; i++) {
      const prevPoint = filteredData[i - 1];
      const currPoint = filteredData[i];
      
      const distance = calculateDistance(
        Number(prevPoint.latitude),
        Number(prevPoint.longitude),
        Number(currPoint.latitude),
        Number(currPoint.longitude)
      );
      
      totalDistance += distance;
      
      const speed = typeof currPoint.speed_over_ground === 'number' && currPoint.speed_over_ground !== null
        ? currPoint.speed_over_ground
        : 0;
      
      if (speed > 0) {
        totalSpeed += speed;
        validSpeedCount++;
      }
      
      maxSpeed = Math.max(maxSpeed, speed);
    }

    const duration = filteredData.length > 0 
      ? new Date(filteredData[filteredData.length - 1].recorded_at).getTime() - 
        new Date(filteredData[0].recorded_at).getTime()
      : 0;

    return {
      totalDistance: totalDistance.toFixed(2),
      avgSpeed: validSpeedCount > 0 ? (totalSpeed / validSpeedCount).toFixed(1) : '0.0',
      maxSpeed: maxSpeed.toFixed(1),
      totalPoints: filteredData.length,
      duration: Math.floor(duration / (1000 * 60 * 60)) 
    };
  }, [filteredData]);

  const summaryData = useMemo(() => {
    return [...filteredData].reverse();
  }, [filteredData]);

  const handleQuickRange = (range: string) => {
    setSelectedQuickRange(range);
    const quickRange = quickRanges.find(r => r.key === range);
    if (quickRange) {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - quickRange.days);
      
      setDateRange({
        start: toLocalDateString(start),
        end: toLocalDateString(end)
      });
    }
  };

  const handleGetLatestUpdate = async () => {
    setIsLoading(true);
    try {
      const response = await beWhereApi.get(`/snapshots/${deviceId}`);
      setBeWhereLatestData(response.data);
      setLastUpdateTime(new Date());
      
      await refreshDeviceData();
    } catch (error) {
      console.error('Failed to refresh data:', error);
      try {
        await refreshDeviceData();
        setLastUpdateTime(new Date());
      } catch (fallbackError) {
        console.error('Fallback refresh also failed:', fallbackError);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const getDeviceStatus = () => {
    if (!latestData) return { status: 'Unknown', color: '#6B7280' };
    
    let lastUpdate;
    if (beWhereLatestData) {
      lastUpdate = new Date(beWhereLatestData.timestamp);
    } else {
      lastUpdate = new Date(latestData.recorded_at);
    }
    
    const now = new Date();
    const minutesDiff = (now.getTime() - lastUpdate.getTime()) / (1000 * 60);
    
    if (minutesDiff < 15) return { status: 'Online', color: '#10B981' };
    if (minutesDiff < 60) return { status: 'Recent', color: '#F59E0B' };
    return { status: 'Offline', color: '#EF4444' };
  };

  const formatBeWhereTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const mapBoundsPositions = useMemo((): [number, number][] => {
    return filteredData
      .filter(
        (point) =>
          point.latitude != null &&
          point.longitude != null &&
          !isNaN(Number(point.latitude)) &&
          !isNaN(Number(point.longitude))
      )
      .map((point) => [Number(point.latitude), Number(point.longitude)]);
  }, [filteredData]);

  const startIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
  });

  const endIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
  });

  const getDeviceTitle = () => {
    if (!deviceId) return "All Devices";
    const device = devices?.find(d => d.device_id === deviceId);
    const name = device?.name || device?.device_name || "Device";
    if (device?.asset_name) return `${name} (Asset: ${device.asset_name})`;
    return name;
  };
  

  const polylinePositions: [number, number][] = filteredData.map(point => [
    Number(point.latitude),
    Number(point.longitude)
  ]);

  const deviceStatus = getDeviceStatus();

  return (
    <PageContainer>
      <Header>
        <h1>{getDeviceTitle()}</h1>
        <div className="subtitle">
          • Device ID: {deviceId || 'All'}
        </div>
      </Header>

      <ControlsContainer>
        <DateRangeSelector>
          <div>
            <label style={{ fontSize: '0.875rem', fontWeight: 500, marginRight: '0.5rem' }}>
              From:
            </label>
            <DateInput
              type="date"
              value={dateRange.start}
              onChange={(e) => {
                setDateRange(prev => ({ ...prev, start: e.target.value }));
                setSelectedQuickRange('');
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: '0.875rem', fontWeight: 500, marginRight: '0.5rem' }}>
              To:
            </label>
            <DateInput
              type="date"
              value={dateRange.end}
              onChange={(e) => {
                setDateRange(prev => ({ ...prev, end: e.target.value }));
                setSelectedQuickRange('');
              }}
            />
          </div>
        </DateRangeSelector>

        <UpdateButton onClick={handleGetLatestUpdate} disabled={isLoading}>
          {isLoading ? (
            <>
              <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeOpacity="0.3"/>
                <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" fill="currentColor"/>
              </svg>
              Updating...
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                <path d="M21 3v5h-5"/>
                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                <path d="M3 21v-5h5"/>
              </svg>
              Get Latest Update
            </>
          )}
        </UpdateButton>

        {lastUpdateTime && (
          <UpdateTime>
            Last updated: {getTimeAgo(lastUpdateTime)}
          </UpdateTime>
        )}

        {isLoading && <LoadingSpinner />}

        {errorDeviceData && (
          <UpdateTime style={{ color: '#DC2626', fontStyle: 'normal' }}>
            Failed to load GPS history: {errorDeviceData}
          </UpdateTime>
        )}
      </ControlsContainer>

      <MapWrapper>
        <MapContainer
          center={
            filteredData.length > 0
              ? [Number(filteredData[0].latitude), Number(filteredData[0].longitude)]
              : [32.806056, -96.688727]
          }
          zoom={15}
          maxZoom={18}
          style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
        >
          <MapResizer />
          <MapRefBridge mapRef={mapRef} />
          <MapBoundsUpdater positions={mapBoundsPositions} />
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            maxZoom={18}
          />

          {filteredData.length > 0 && (
            <>
              <Polyline
                positions={polylinePositions}
                color={COLORS.track}
                weight={4}
                opacity={0.8}
                smoothFactor={1}
              />

              {filteredData.length > 2 && filteredData.slice(1, -1).map((point, index) => (
                <CircleMarker
                  key={point.id}
                  center={[Number(point.latitude), Number(point.longitude)]}
                  radius={selectedPointId === point.id ? 10 : 6}
                  fillColor={selectedPointId === point.id ? '#FF6B00' : COLORS.track}
                  color={COLORS.white}
                  weight={selectedPointId === point.id ? 3 : 2}
                  opacity={1}
                  fillOpacity={0.9}
                  eventHandlers={{
                    mouseover: () => setHoveredPoint(point.id),
                    mouseout: () => setHoveredPoint(null),
                  }}
                >
                  <Popup>
                    <div>
                      <strong>Time:</strong> {formatTimestamp(point.recorded_at)}<br />
                      <strong>Speed:</strong> {point.speed_over_ground || 'N/A'} km/h<br />
                      <strong>Battery:</strong> {point.voltage}V<br />
                      <strong>Satellites:</strong> {point.satellites_in_view || 0}<br />
                    </div>
                  </Popup>
                </CircleMarker>
              ))}

              {filteredData.length <= 2 && filteredData.map((point, index) => (
                <CircleMarker
                  key={point.id}
                  center={[Number(point.latitude), Number(point.longitude)]}
                  radius={selectedPointId === point.id ? 10 : 6}
                  fillColor={selectedPointId === point.id ? '#FF6B00' : COLORS.track}
                  color={COLORS.white}
                  weight={selectedPointId === point.id ? 3 : 2}
                  opacity={1}
                  fillOpacity={0.9}
                  eventHandlers={{
                    mouseover: () => setHoveredPoint(point.id),
                    mouseout: () => setHoveredPoint(null),
                  }}
                >
                  <Popup>
                    <div>
                      <strong>Time:</strong> {formatTimestamp(point.recorded_at)}<br />
                      <strong>Speed:</strong> {point.speed_over_ground || 'N/A'} km/h<br />
                      <strong>Battery:</strong> {point.voltage}V<br />
                      <strong>Satellites:</strong> {point.satellites_in_view || 0}<br />
                    </div>
                  </Popup>
                </CircleMarker>
              ))}

              {filteredData.length > 0 && (
                <Marker
                  position={[Number(filteredData[0].latitude), Number(filteredData[0].longitude)]}
                  icon={startIcon}
                >
                  <Popup>
                    <div>
                      <strong>Journey Start</strong><br />
                      <strong>Time:</strong> {formatTimestamp(filteredData[0].recorded_at)}<br />
                      <strong>Location:</strong> {Number(filteredData[0].latitude).toFixed(6)}, {Number(filteredData[0].longitude).toFixed(6)}
                    </div>
                  </Popup>
                </Marker>
              )}

              {filteredData.length > 1 && (
                <Marker
                  position={[Number(filteredData[filteredData.length - 1].latitude), Number(filteredData[filteredData.length - 1].longitude)]}
                  icon={endIcon}
                >
                  <Popup>
                    <div>
                      <strong>Latest Position</strong><br />
                      <strong>Time:</strong> {formatTimestamp(filteredData[filteredData.length - 1].recorded_at)}<br />
                      <strong>Location:</strong> {Number(filteredData[filteredData.length - 1].latitude).toFixed(6)}, {Number(filteredData[filteredData.length - 1].longitude).toFixed(6)}
                    </div>
                  </Popup>
                </Marker>
              )}
            </>
          )}

          {/* Show BeWhere latest position if available */}
          {beWhereLatestData && (
            <Marker
              position={[beWhereLatestData.latitude, beWhereLatestData.longitude]}
              icon={endIcon}
            >
              <Popup>
                <div>
                  <strong>Current Position</strong><br />
                  <strong>Time:</strong> {formatBeWhereTimestamp(beWhereLatestData.timestamp)}<br />
                  <strong>Location:</strong> {beWhereLatestData.latitude.toFixed(6)}, {beWhereLatestData.longitude.toFixed(6)}<br />
                  <strong>Speed:</strong> {beWhereLatestData.speed} km/h<br />
                </div>
              </Popup>
            </Marker>
          )}
        </MapContainer>

        <PanelToggleContainer>
          <ToggleButton 
            onClick={() => setShowLatestData(!showLatestData)}
            active={showLatestData}
            title="Toggle Latest Data Panel"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3"/>
              <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1"/>
            </svg>
          </ToggleButton>
          
          <ToggleButton
            onClick={() => setShowTripStats(!showTripStats)}
            active={showTripStats}
            title="Toggle Trip Statistics Panel"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 3v18h18"/>
              <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"/>
            </svg>
          </ToggleButton>

          <ToggleButton
            onClick={() => setShowSummary(!showSummary)}
            active={showSummary}
            title="Toggle Location Summary"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="8" y1="6" x2="21" y2="6"/>
              <line x1="8" y1="12" x2="21" y2="12"/>
              <line x1="8" y1="18" x2="21" y2="18"/>
              <line x1="3" y1="6" x2="3.01" y2="6"/>
              <line x1="3" y1="12" x2="3.01" y2="12"/>
              <line x1="3" y1="18" x2="3.01" y2="18"/>
            </svg>
          </ToggleButton>
        </PanelToggleContainer>

        {showLatestData && latestData && (
          <LatestDataPanel>
            <PanelHeader>
              <h3>Latest Data {beWhereLatestData ? '(Live)' : ''}</h3>
              <CloseButton onClick={() => setShowLatestData(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </CloseButton>
            </PanelHeader>
            
            <LatestDataGrid>
              {beWhereLatestData ? (
                <>
                  <DataItem>
                    <DataLabel>Last Update</DataLabel>
                    <DataValue>{formatBeWhereTimestamp(beWhereLatestData.timestamp)}</DataValue>
                  </DataItem>
                  
                  <DataItem>
                    <DataLabel>Battery Level</DataLabel>
                    <DataValue>{beWhereLatestData.batteryLevel}V</DataValue>
                  </DataItem>
                  
               
                  
          
                  
                  <DataItem>
                    <DataLabel>Status</DataLabel>
                    <DataValue>{beWhereLatestData.status}</DataValue>
                  </DataItem>
                  
                  <DataItem style={{ gridColumn: '1 / -1' }}>
                    <DataLabel>Address</DataLabel>
                    <DataValue>{beWhereLatestData.address}</DataValue>
                  </DataItem>
                  
                  <DataItem style={{ gridColumn: '1 / -1' }}>
                    <DataLabel>Coordinates</DataLabel>
                    <DataValue>
                      {beWhereLatestData.latitude.toFixed(4)}, {beWhereLatestData.longitude.toFixed(4)}
                    </DataValue>
                  </DataItem>
                </>
              ) : (
                <>
                  <DataItem>
                    <DataLabel>Last Update</DataLabel>
                    <DataValue>{formatTimestamp(latestData.recorded_at)}</DataValue>
                  </DataItem>
                  
                  <DataItem>
                    <DataLabel>Battery Level</DataLabel>
                    <DataValue>{latestData.voltage}V</DataValue>
                  </DataItem>
                  
                  <DataItem>
                    <DataLabel>Satellites</DataLabel>
                    <DataValue>{latestData.satellites_in_view || 0}</DataValue>
                  </DataItem>
                  
                  <DataItem style={{ gridColumn: '1 / -1' }}>
                    <DataLabel>Coordinates</DataLabel>
                    <DataValue>
                      {Number(latestData.latitude).toFixed(4)}, {Number(latestData.longitude).toFixed(4)}
                    </DataValue>
                  </DataItem>
                </>
              )}
            </LatestDataGrid>
          </LatestDataPanel>
        )}

        {showTripStats && (
          <StatsPanel>
            <PanelHeader>
              <h3>Trip Statistics</h3>
              <CloseButton onClick={() => setShowTripStats(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </CloseButton>
            </PanelHeader>
            
            {filteredData.length > 0 ? (
              <>
                <StatItem>
                  <span className="label">Total Distance</span>
                  <span className="value">{stats.totalDistance} km</span>
                </StatItem>
                <StatItem>
                  <span className="label">Duration</span>
                  <span className="value">{stats.duration}h</span>
                </StatItem>
              </>
            ) : (
              <EmptyMessage>
                No trip data available for selected date range
              </EmptyMessage>
            )}
          </StatsPanel>
        )}

        {showSummary && (
          <SummaryPanel>
            <PanelHeader>
              <h3>Location Timeline</h3>
              <CloseButton onClick={() => setShowSummary(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </CloseButton>
            </PanelHeader>
            <IntervalSelector>
              {[
                { label: '1 hour', value: 60 },
                { label: '5 min', value: 5 },
                { label: '30 min', value: 30 },
                { label: '1 day', value: 1440 },
              ].map(opt => (
                <IntervalBtn
                  key={opt.value}
                  active={summaryInterval === opt.value}
                  onClick={() => setSummaryInterval(opt.value)}
                >
                  {opt.label}
                </IntervalBtn>
              ))}
              {summaryInterval !== 60 && (
                <IntervalBtn
                  active={false}
                  onClick={() => { setSummaryInterval(60); setSelectedPointId(null); }}
                  style={{ marginLeft: 'auto', color: '#EF4444', borderColor: '#EF4444' }}
                >
                  Clear
                </IntervalBtn>
              )}
            </IntervalSelector>
            <SummaryList>
              {summaryData.length === 0 ? (
                <EmptyMessage>No data for selected range</EmptyMessage>
              ) : (
                summaryData.map(point => (
                  <SummaryItem
                    key={point.id}
                    active={selectedPointId === point.id}
                    onClick={() => {
                      setSelectedPointId(point.id);
                      mapRef.current?.flyTo(
                        [Number(point.latitude), Number(point.longitude)],
                        17,
                        { animate: true, duration: 0.8 }
                      );
                    }}
                  >
                    <SummaryTime>{formatTimestamp(point.recorded_at)}</SummaryTime>
                    <SummaryCoords>
                      {Number(point.latitude).toFixed(5)}, {Number(point.longitude).toFixed(5)}
                      {point.speed_over_ground ? ` • ${point.speed_over_ground} km/h` : ''}
                    </SummaryCoords>
                  </SummaryItem>
                ))
              )}
            </SummaryList>
          </SummaryPanel>
        )}

        <Legend>
          <LegendItem>
            <LegendDot color={COLORS.success} />
            <LegendText>Start Point</LegendText>
          </LegendItem>
          <LegendItem>
            <LegendDot color={COLORS.danger} />
            <LegendText>End Point</LegendText>
          </LegendItem>
          <LegendItem>
            <LegendLine color={COLORS.track} />
            <LegendText>Route Track</LegendText>
          </LegendItem>
        </Legend>

        {filteredData.length === 0 && (
          <EmptyState>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
            </svg>
            <h3>No tracking data available</h3>
            <p>No GPS data found for the selected date range. Try adjusting the date filters.</p>
          </EmptyState>
        )}
      </MapWrapper>
    </PageContainer>
  );
};



export default MapComponent;